import React, { PropsWithChildren, useRef, useState, useEffect, useMemo, Fragment } from 'react';
import styled from '@emotion/styled';
import { AnchorLayout } from 'components/views/atoms/AnchorLayout';
import { EmergencyButton } from 'components/views/molecules/Cockpit/EmergencyButton';
import { SettingsButton } from 'components/views/molecules/Cockpit/SettingsButton';
import { HelpButton } from 'components/views/molecules/Cockpit/HelpButton';
import { SpeakerButton } from 'components/views/molecules/Cockpit/SpeakerButton';
import { withToggleSpeaker } from 'components/views/molecules/ToggleSpeaker';
import { TelephoneButton, TelephoneState } from 'components/views/molecules/Cockpit/TelephoneButton';
import { RecordingButton } from 'components/views/molecules/Cockpit/RecordingButton';
import { AccelSettingButton } from 'components/views/molecules/Cockpit/AccelSettingButton';
import { keyframes } from '@emotion/react';
import { AttentionSound } from 'components/views/atoms/sounds/AttentionSound';
import { StandByFrame } from 'components/views/molecules/StandByFrame';
import { ReloadButton } from 'components/views/molecules/Cockpit/ReloadButton';
import { KnobbedDrawer } from 'components/views/atoms/KnobbedDrawer';
import { HeartbeatResult } from 'services/useStandBy';
import { TelephonePanel } from 'components/views/molecules/Cockpit/TelephonePanel';

const Frame = styled.div({
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
});

const Center = styled.div({
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    ['>*']: {
        width: '100%',
    },
});

const Overlay = styled.div({
    pointerEvents: 'none',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
});

const Upper = styled.div({
    flexGrow: 10,
});

const Lower_ = styled.div({
    margin: '10px 20px 10px 40px',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    ['> *']: {
        pointerEvents: 'auto',
    },
});

function Lower<T>(props: React.PropsWithChildren<T>) {
    const { children } = props;
    return <KnobbedDrawer><Lower_>{children}</Lower_></KnobbedDrawer>;
}

const AnchorBoxFrame = styled.div<{ width: string }>(props => ({
    width: props.width,
}));

export function useTapAndMagnify<T extends HTMLElement>(defaultWidth: string, largeWidth: string): [React.Ref<T>, string] {
    const [width, setWidth] = useState(defaultWidth);

    const frameRef = useRef<T>(null);
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) {
            console.error('missing ref');
            return;
        }

        const enterListener = () => {
            setWidth(largeWidth);
        };

        const leaveListener = () => {
            setWidth(defaultWidth);
        };

        frame.addEventListener('mousedown', enterListener);
        frame.addEventListener('mouseup', leaveListener);

        return () => {
            frame.removeEventListener('mouseup', leaveListener);
            frame.removeEventListener('mousedown', enterListener);
        };
    }, [defaultWidth, largeWidth]);

    return [frameRef, width];
}

export function useDragResize<T extends HTMLElement>(defaultWidth: string): [React.Ref<T>, string] {
    const [width, setWidth] = useState(defaultWidth);

    const frameRef = useRef<T>(null);
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) {
            console.error('missing ref');
            return;
        }

        let dragging: { x: number; y: number; w: number; h: number; cx: number } | undefined = undefined;

        const enterListener = (e: MouseEvent) => {
            e.preventDefault();
            dragging = {
                x: e.clientX,
                y: e.clientY,
                w: frame.clientWidth,
                h: frame.clientHeight,
                cx: frame.clientLeft + frame.clientWidth / 2,
            };
        };

        const leaveListener = () => {
            dragging = undefined;
        };

        const moveListener = (e: MouseEvent) => {
            if (!dragging) return;
            const w = e.x > dragging.cx
                ? dragging.w + (e.clientX - dragging.x)
                : dragging.w - (e.clientX - dragging.x)
                ;
            if (w > 10) {
                setWidth(`${w}px`);
            }
        };

        frame.addEventListener('mousedown', enterListener);
        window.addEventListener('mouseup', leaveListener);
        window.addEventListener('mousemove', moveListener);

        return () => {
            window.removeEventListener('mousemove', moveListener);
            window.removeEventListener('mouseup', leaveListener);
            frame.removeEventListener('mousedown', enterListener);
        };
    }, []);

    return [frameRef, width];
}

type AnchorBoxProps = {
    defaultWidth?: string;
}

const AnchorBox = (props: PropsWithChildren<AnchorBoxProps>) => {
    const [frameRef, width] = useDragResize<HTMLDivElement>(props.defaultWidth || '25vw');
    // const [frameRef, width] = useTapAndMagnify<HTMLDivElement>('25vw', '50vw');

    return (
        <AnchorBoxFrame width={width} ref={frameRef}>{props.children}</AnchorBoxFrame>
    );
};

const IconTray = styled.div({
    //position: 'absolute',
    //bottom: '0px',
    //width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '1.5em',
});

const flashing = keyframes({
    from: {
        borderColor: 'red',
    },
    to: {
        borderColor: 'transparent',
    }
});

const AlertFrame = styled.div<{ alert: 'green' | 'red' }>(props => ({
    boxSizing: 'border-box',
    border: props.alert === 'green' ? 'solid thick transparent' : 'solid thick',
    animation: props.alert === 'green' ? undefined : `${flashing} 1s ease alternate infinite`,
    position: 'relative',

    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    minWidth: '710px',
    maxWidth: '1400px',
    margin: '0px 40px',
    borderRadius: '5px',
}));

const AlertTickerBox = styled.div({
    'position': 'absolute',
    'bottom': '100%',
    'border': 'solid thick transparent',
});

const CenterPanel = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    flexGrow: 1,
});

const SteeringFrame = styled.div({
    margin: '10px 20px',
    height: '100%',
    flexGrow: 2,
});

const SpeedMeterFrame = styled.div({
    margin: '10px 20px',
    flexGrow: 1,
});

const DriveInfoFrame = styled.div({
    margin: '10px 20px',
    height: '100%',
    flexGrow: 2,
});

const DriveSwitchFrame = styled.div({
    margin: '10px 20px',
    height: '100%',
    flexGrow: 2,
});

const InfoPanel = styled.div({
    boxSizing: 'border-box',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '5px',
    padding: '1ex 1.5ex',
    color: 'white',
});

const ToggleSpeaker = withToggleSpeaker(SpeakerButton);

export type CockpitAnchorLayout<T> = {
    center: T[];
    top: T[];
    left: T[];
    right: T[];
}

export type CockpitProps<T> = {
    layout: CockpitAnchorLayout<T>;
    getElementForAnchor: (id: T, stretch?: boolean) => JSX.Element;
    microphoneStream?: MediaStream;
    vehicleStatus: JSX.Element;
    steeringIndicator: JSX.Element;
    speedMeter: JSX.Element;
    driveInfo: JSX.Element;
    driveSwitch: JSX.Element;
    onEmergencyClick?: () => void;
    onSettingsClick?: () => void;
    onHelpClick?: () => void;
    telephone?: TelephoneState;
    onTelephoneClick?: () => void;
    telephones?: Record<string, TelephoneState>
    onTelephonesClick?: (tag: string) => void;
    isReloading?: boolean;
    onReloadClick?: () => void;
    isRecording?: boolean | null;
    onRecordingClick?: () => void;
    onAccelSettingClick?: () => void;
    alerting?: 'green' | 'red';
    alertTicker?: JSX.Element;
    standby?: boolean;
    onStandBy?: () => void;
    heartbeatStatus?: HeartbeatResult;
    isStandByAllowed?: boolean;
    widgets: JSX.Element[];
}

export function Cockpit<T>(props: CockpitProps<T>) {
    const { layout, getElementForAnchor } = props;
    const { onHelpClick, onEmergencyClick } = props;
    const { isReloading = false, onReloadClick } = props;
    const { telephone, onTelephoneClick } = props;
    const { isRecording = null, onRecordingClick } = props;
    const { onAccelSettingClick } = props;
    const { alerting: alert = 'green', alertTicker } = props;
    const { standby = false, onStandBy, heartbeatStatus, isStandByAllowed } = props;
    const { telephones, onTelephonesClick } = props;

    const center = useMemo(() => getElementForAnchor(layout.center[0], true), [layout, getElementForAnchor]);

    const anchors = useMemo(() => ({
        top: layout.top.map((id, i) => <AnchorBox key={i} defaultWidth='16vw'>{getElementForAnchor(id)}</AnchorBox>),
        left: layout.left.map((id, i) => <AnchorBox key={i} defaultWidth='14vw'>{getElementForAnchor(id)}</AnchorBox>),
        right: layout.right.map((id, i) => <AnchorBox key={i} defaultWidth='14vw'>{getElementForAnchor(id)}</AnchorBox>),
    }), [layout, getElementForAnchor]);

    const icons = [
        onReloadClick
            ? <ReloadButton key='reload' reloading={isReloading} onClick={onReloadClick} />
            : <Fragment key='reload'></Fragment>,
        <ToggleSpeaker key='speaker' stream={props.microphoneStream ?? null} />,
        telephones && Object.keys(telephones).length > 0 ? (
            <TelephonePanel
                onClick={onTelephonesClick}
                telephones={telephones}
            />
        ) : telephone ? (
            <TelephoneButton key='telephone' state={telephone} onClick={onTelephoneClick} />
        ) : undefined,
        onRecordingClick
            ? <RecordingButton key='recording' recording={isRecording === true} disabled={isRecording === null} onClick={onRecordingClick} />
            : <Fragment key='recording'></Fragment>,
        <SettingsButton key='settings' onClick={props.onSettingsClick} />,
        onAccelSettingClick
            ? <AccelSettingButton key='accel' onClick={onAccelSettingClick} />
            : undefined,
        onHelpClick
            ? <HelpButton key='help' onClick={onHelpClick} />
            : <Fragment key='help'></Fragment>,
        onEmergencyClick
            ? <EmergencyButton key='emergency' onClick={onEmergencyClick} />
            : <Fragment key='emergency'></Fragment>,
    ];

    useEffect(() => {
        console.info('Autoware heartbeat status', heartbeatStatus?.status);
    }, [heartbeatStatus?.status]);

    return (
        <Frame>
            <Center>{center}</Center>
            <Overlay>
                <Upper>
                    <AnchorLayout anchors={anchors} />
                </Upper>
                <Lower>
                    <InfoPanel>
                        {props.vehicleStatus}
                    </InfoPanel>
                    <AlertFrame alert={alert}>
                        {
                            alert != 'green'
                                ? <AttentionSound />
                                : <></>
                        }
                        {alertTicker && alert != 'green'
                            ? <AlertTickerBox>{alertTicker}</AlertTickerBox>
                            : <></>
                        }
                        <StandByFrame darkMode={true} standby={standby} onClick={onStandBy} heartbeatStatus={heartbeatStatus} isStandByAllowed={isStandByAllowed}>
                            <CenterPanel>
                                <SteeringFrame>{props.steeringIndicator}</SteeringFrame>
                                <SpeedMeterFrame>{props.speedMeter}</SpeedMeterFrame>
                                <DriveInfoFrame>{props.driveInfo}</DriveInfoFrame>
                                <DriveSwitchFrame>{props.driveSwitch}</DriveSwitchFrame>
                            </CenterPanel>
                        </StandByFrame>
                    </AlertFrame>
                    <IconTray>{icons}</IconTray>
                </Lower>
            </Overlay>
            {
                props.widgets.map(c => c)
            }
        </Frame>
    );
}
